import { FormContent, PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import SettingsLayout from "@/layouts/settings_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { locationBayPath, locationBaysPath } from "@/routes";
import type { Bay } from "./types";
import BookingSetupReturn, {
  BookingReturnField,
  type BookingReturnContext,
} from "@/components/booking-setup-return";
import { FormErrors } from "@/components/forms/form-errors";

export default function BayForm({
  bay,
  booking_return,
}: {
  bay: Bay | null;
  booking_return?: BookingReturnContext;
}) {
  const { auth, errors } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const title = t(bay ? "bays.edit" : "bays.add");
  return (
    <SettingsLayout>
      <Head title={title} />
      <PageCanvas padded={false}>
        <FormContent>
          <BookingSetupReturn context={booking_return} />
          <h1 className="text-page font-display">{title}</h1>
        </FormContent>
        <Card data-layout-frame="bay-editor">
          <CardContent className="pt-6">
            <FormContent>
              <Form
                key={bay?.revision || "new"}
                action={
                  bay
                    ? locationBayPath(locationId, bay.id)
                    : locationBaysPath(locationId)
                }
                method={bay ? "patch" : "post"}
              >
                {({ processing }) => (
                  <div className="space-y-4">
                    <BookingReturnField context={booking_return} />
                    <FormErrors
                      errors={errors}
                      targets={{ name: "bay-name", enabled: "bay-enabled" }}
                    />
                    {bay && (
                      <input
                        type="hidden"
                        name="revision"
                        value={bay.revision}
                      />
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="bay-name">{t("bays.name")}</Label>
                      <Input
                        autoFocus
                        required
                        id="bay-name"
                        name="bay[name]"
                        defaultValue={bay?.name || ""}
                        maxLength={200}
                        aria-invalid={!!errors?.name}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bay-enabled">
                        {t("bays.availability")}
                      </Label>
                      <Select
                        name="bay[enabled]"
                        defaultValue={bay?.enabled === false ? "false" : "true"}
                        items={{
                          true: t("bays.enabled"),
                          false: t("bays.disabled"),
                        }}
                      >
                        <SelectTrigger
                          id="bay-enabled"
                          aria-describedby="bay-help"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">
                            {t("bays.enabled")}
                          </SelectItem>
                          <SelectItem value="false">
                            {t("bays.disabled")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p
                        id="bay-help"
                        className="text-muted-foreground text-sm"
                      >
                        {t("bays.availability_help")}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Button type="submit" disabled={processing}>
                        {t("common.save")}
                      </Button>
                      <Button
                        variant="outline"
                        render={
                          <Link
                            href={
                              booking_return?.path ||
                              locationBaysPath(locationId)
                            }
                          />
                        }
                      >
                        {t("inventory.cancel")}
                      </Button>
                    </div>
                  </div>
                )}
              </Form>
            </FormContent>
          </CardContent>
        </Card>
      </PageCanvas>
    </SettingsLayout>
  );
}
